/**
 * Room Cloud Function service (PPL-17).
 *
 * Wraps all 20 deployed europe-west1 mediation callables, plus three
 * Firestore `onSnapshot` subscribers used by the prep thread and the joint
 * room. Mirrors `circleService.ts` (per-callable refs at top, exported async
 * functions at the bottom) and `messageService.subscribeToMessages` for the
 * onSnapshot shape.
 *
 * Auth model:
 *  - 19 of the 20 callables require auth (caller passes Firebase ID token via
 *    the SDK, transparently).
 *  - `resolveMediationInviteByTokenEurW` is the UNAUTHENTICATED public lookup
 *    behind the `/i/:token` landing — anyone can call it with a token; the
 *    response is locked to a safe public view. The callable itself permits
 *    unauth (precedent: `getPublicCirclesEurW`); the client wraps it with
 *    plain `httpsCallable` exactly the same as the authenticated ones — the
 *    Firebase SDK simply omits the auth header when no user is signed in.
 */

import {
  collection,
  doc,
  limit as fbLimit,
  onSnapshot,
  orderBy,
  query,
  Unsubscribe,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { ref, uploadBytes } from 'firebase/storage';
import { FIREBASE_FUNCTIONS, FIREBASE_STORAGE, FIRESTORE_DB } from '../firebaseConfig';
import type {
  GetMyRoomsResponse,
  Room,
  RoomCheckinAnswer,
  RoomChannel,
  RoomExitSummary,
  RoomInvitePublicView,
  RoomMessage,
  RoomState,
} from '../types/room';

// ─── Callable refs ───────────────────────────────────────────────────────────

// PPL-60 — accepts inviteeId OR inviteeEmail; isPlaceholder flags the
// not-on-pplkpr case; token + inviteUrl are returned for any out-of-band
// delivery (email/link).
const createRoomInviteFn = httpsCallable<
  {
    inviteeId?: string;
    inviteeEmail?: string;
    // Display name the inviter typed for the invitee — shown back to the
    // inviter on /rooms while the invite is pending, and used as the email
    // greeting for placeholder (not-on-pplkpr) invites.
    inviteeName?: string;
    reason: string;
    channel?: RoomChannel;
    inviterRelationship?: string;
    // Analytics attribution: 'composer' (rooms page) vs 'f8_propose_session'
    // (f8 mediation compose card). Validated server-side; null for old clients.
    source?: string;
  },
  {
    success: boolean;
    mediationId: string;
    state: string;
    created: boolean;
    /** created:false + still-invited + caller is inviter → email re-delivered. */
    resent?: boolean;
    /** Resend skipped: one already went out within the last hour. */
    resendThrottled?: boolean;
    isPlaceholder?: boolean;
    token?: string;
    inviteUrl?: string;
    reason?: string;
  }
>(FIREBASE_FUNCTIONS, 'createMediationInviteEurW');

const respondToRoomInviteFn = httpsCallable<
  { mediationId: string; response: 'accept' | 'decline' },
  { success: boolean; mediationId: string; state: string }
>(FIREBASE_FUNCTIONS, 'respondToMediationInviteEurW');

/**
 * UNAUTHENTICATED. The callable itself permits unauth (precedent:
 * `getPublicCirclesEurW`); the SDK transparently omits the auth header when
 * no user is signed in.
 */
const resolveRoomInviteByTokenFn = httpsCallable<
  { token: string },
  RoomInvitePublicView
>(FIREBASE_FUNCTIONS, 'resolveMediationInviteByTokenEurW');

const acceptRoomInviteByTokenFn = httpsCallable<
  { token: string },
  { success: boolean; mediationId: string }
>(FIREBASE_FUNCTIONS, 'acceptMediationInviteByTokenEurW');

/**
 * UNAUTHENTICATED (like the resolver) — declining never requires signup.
 * Token possession is the capability; works whether or not the visitor is
 * logged in or bound to the mediation yet.
 */
const declineRoomInviteByTokenFn = httpsCallable<
  { token: string },
  { success: boolean; state: string; endedReason: string | null }
>(FIREBASE_FUNCTIONS, 'declineMediationInviteByTokenEurW');

const getMyRoomsFn = httpsCallable<
  Record<string, never>,
  GetMyRoomsResponse
>(FIREBASE_FUNCTIONS, 'getMyMediationsEurW');

const startRoomPrepFn = httpsCallable<
  { mediationId: string },
  { success: boolean; opened: boolean; messageId?: string }
>(FIREBASE_FUNCTIONS, 'startMediationPrepEurW');

const sendRoomPrepMessageFn = httpsCallable<
  { mediationId: string; text: string },
  { success: boolean; messageId?: string; f8Message?: RoomMessage }
>(FIREBASE_FUNCTIONS, 'sendMediationPrepMessageEurW');

const endRoomPrepSoloFn = httpsCallable<
  { mediationId: string },
  { success: boolean }
>(FIREBASE_FUNCTIONS, 'endMediationPrepSoloEurW');

// PPL-65 — dynamic suggested-reply chips for the prep page. Replaces hardcoded
// `cfg.chips(...)` strings; context-aware (reason + side + recent prep).
const generateRoomPrepChipsFn = httpsCallable<
  { mediationId: string },
  { success: boolean; chips: string[] }
>(FIREBASE_FUNCTIONS, 'generateRoomPrepChipsEurW');

// Authenticated, mediation-scoped resolver for the in-app /rooms/:id/invite
// page. Mirrors the public token resolver shape so the receive page can
// render the inviter's firstName (Cam) instead of falling back to the
// handle (@SunsetMediator) — getPublicUserProfileEurW is intentionally
// handle-only everywhere else.
const getMediationInviterFn = httpsCallable<
  { mediationId: string },
  {
    success: boolean;
    inviter: {
      tetherHandle: string | null;
      firstName: string | null;
      profileImageUrl: string | null;
    };
    reason: string;
  }
>(FIREBASE_FUNCTIONS, 'getMediationInviterEurW');

// Mediation-scoped resolver for both participants relative to the caller
// (`self` = caller, `counterpart` = the other party), so room screens render
// real first names from either side instead of the handle-only
// getPublicUserProfileEurW.
interface RoomParticipantDisplay {
  tetherHandle: string | null;
  firstName: string | null;
  profileImageUrl: string | null;
}
const getMediationCounterpartFn = httpsCallable<
  { mediationId: string },
  {
    success: boolean;
    self: RoomParticipantDisplay;
    counterpart: RoomParticipantDisplay;
  }
>(FIREBASE_FUNCTIONS, 'getMediationCounterpartEurW');

const updateRoomConsentFn = httpsCallable<
  { mediationId: string; refPrep?: boolean; refHistory?: boolean; refPatterns?: boolean },
  // `bothSigned` flips true when the *second* side confirms (both have signedAt),
  // which is how the consent screen decides whether to send the user to the
  // waiting room (first side) or straight into the joint chat (last side).
  { success: boolean; state: RoomState; bothSigned: boolean; rulesSet: boolean }
>(FIREBASE_FUNCTIONS, 'updateMediationConsentEurW');

const setRoomReadyFn = httpsCallable<
  { mediationId: string },
  { success: boolean; bothReady: boolean }
>(FIREBASE_FUNCTIONS, 'setMediationReadyEurW');

const sendRoomMessageFn = httpsCallable<
  {
    mediationId: string;
    text?: string;
    // PPL-186 image turn: messageType 'image' + the caller's uploaded object
    // path; text becomes the optional caption. PPL-187: fulfillsRequestId ties
    // the image to an open picture request.
    messageType?: 'text' | 'image';
    storagePath?: string;
    fulfillsRequestId?: string;
  },
  // `success: false` + `reason` is the moderation-block path (NOT a thrown error),
  // so callers must check `success` explicitly or the blocked message vanishes silently.
  { success: boolean; messageId?: string; reason?: string }
>(FIREBASE_FUNCTIONS, 'sendMediationMessageEurW');

// PPL-187 — request-a-picture skill.
const requestRoomPictureFn = httpsCallable<
  { mediationId: string },
  { success: boolean; messageId?: string }
>(FIREBASE_FUNCTIONS, 'requestRoomPictureEurW');

const respondRoomPictureRequestFn = httpsCallable<
  { mediationId: string; requestMessageId: string },
  { success: boolean; status?: string; alreadyResolved?: boolean }
>(FIREBASE_FUNCTIONS, 'respondRoomPictureRequestEurW');

// Emoji reaction on a shared-room message — mirrors the 1:1 chat's
// sendEmojiReactionToMessageEurW (same allowlist, one reaction per user,
// server replaces on re-react). Reactions arrive via the existing
// roomMessages onSnapshot; no extra subscription needed.
const sendRoomReactionFn = httpsCallable<
  { mediationId: string; messageId: string; emoji: string },
  { success: boolean; timestamp: string }
>(FIREBASE_FUNCTIONS, 'sendMediationRoomReactionEurW');

const requestMediatorTurnFn = httpsCallable<
  { mediationId: string },
  { success: boolean; throttled?: boolean }
>(FIREBASE_FUNCTIONS, 'requestMediatorTurnEurW');

const markRoomSeenFn = httpsCallable<
  { mediationId: string },
  { success: boolean }
>(FIREBASE_FUNCTIONS, 'markMediationRoomSeenEurW');

const submitRoomCheckinFn = httpsCallable<
  { mediationId: string; answer: RoomCheckinAnswer },
  { success: boolean }
>(FIREBASE_FUNCTIONS, 'submitMediationCheckinEurW');

const endRoomKeepSummaryFn = httpsCallable<
  { mediationId: string },
  { success: boolean }
>(FIREBASE_FUNCTIONS, 'endMediationKeepSummaryEurW');

const pauseRoomFn = httpsCallable<
  { mediationId: string },
  { success: boolean; transitioned: boolean }
>(FIREBASE_FUNCTIONS, 'pauseMediationEurW');

const resumeRoomFn = httpsCallable<
  { mediationId: string },
  { success: boolean; transitioned: boolean }
>(FIREBASE_FUNCTIONS, 'resumeMediationEurW');

const deleteRoomForBothFn = httpsCallable<
  { mediationId: string },
  { success: boolean; endedReason: string }
>(FIREBASE_FUNCTIONS, 'deleteMediationForBothEurW');

const retrySynthesisFn = httpsCallable<
  { mediationId: string },
  { success: boolean }
>(FIREBASE_FUNCTIONS, 'retrySynthesisEurW');

// ─── Subscription metadata ───────────────────────────────────────────────────

/**
 * The hard cap on how many messages a single `onSnapshot` query retrieves.
 * Exported so consumer hooks/pages can reference it in banner copy.
 */
export const MESSAGE_LIMIT = 100;

export interface SubscriptionMeta {
  /** True when the snapshot hit the query limit; older messages are not shown. */
  atLimit: boolean;
}

// ═════════════════════════════════════════════════════════════════════════════
// Exported service functions
// ═════════════════════════════════════════════════════════════════════════════

// ─── Invitation ──────────────────────────────────────────────────────────────

// PPL-129 — inviterRelationship enum keys. Mirrors INVITER_RELATIONSHIPS in
// cloud-functions/functions/src/mediation/mediation.model.ts; the callable
// REJECTS unknown values, so free-text rels must go through
// toInviterRelationship() before being sent.
const INVITER_RELATIONSHIPS = new Set([
  'new-connection',
  'friend', 'best-friend', 'close-friend', 'family-friend',
  'partner', 'ex-partner', 'crush', 'date-partner',
  'spouse', 'fiance', 'boyfriend', 'girlfriend',
  'parent', 'mother', 'father', 'step-parent',
  'child', 'son', 'daughter', 'step-child',
  'sibling', 'brother', 'sister', 'half-sibling', 'step-sibling',
  'grandparent', 'grandchild',
  'uncle', 'aunt', 'cousin', 'nephew', 'niece',
  'in-law', 'parent-in-law', 'sibling-in-law', 'child-in-law',
  'colleague', 'boss', 'employee', 'mentor', 'mentee',
  'classmate', 'teacher', 'student',
  'neighbour', 'acquaintance',
  'work-colleague', 'ex-work-colleague', 'business-partner',
  'father-in-law', 'mother-in-law', 'brother-in-law', 'sister-in-law',
  'husband', 'wife', 'ex-wife', 'ex-husband',
  'employer', 'client', 'customer', 'supplier', 'competitor',
  'grandmother', 'grandfather', 'grandson', 'grand-daughter',
  'great-grandchild', 'great-grandson', 'great-granddaughter',
  'stepmother', 'stepfather', 'stepbrother', 'stepsister',
  'other',
]);

/**
 * PPL-129 — map a composer relationship string (camelCase RelationshipType,
 * the synthetic 'new connection', or the 'no relationship set' placeholder)
 * to a kebab-case inviterRelationship enum key. Placeholder / empty →
 * undefined (field omitted from the payload); unrecognised → 'other'.
 */
export function toInviterRelationship(rel: string | null | undefined): string | undefined {
  const raw = (rel || '').trim();
  if (!raw || raw.toLowerCase() === 'no relationship set') return undefined;
  const kebab = raw
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/[\s_]+/g, '-')
    .toLowerCase();
  return INVITER_RELATIONSHIPS.has(kebab) ? kebab : 'other';
}

export async function createRoomInvite(params: {
  inviteeId?: string;
  inviteeEmail?: string;
  inviteeName?: string;
  reason: string;
  channel?: RoomChannel;
  inviterRelationship?: string;
  source?: string;
}) {
  const res = await createRoomInviteFn(params);
  return res.data;
}

// PPL-54 — handle → user lookup so the /rooms inline composer can call
// createRoomInvite directly for linked contacts.
const resolveTetherHandleFn = httpsCallable<
  { handle: string },
  | { found: false }
  | { found: true; uid: string; displayName: string; tetherHandle: string; profileImageUrl: string | null }
>(FIREBASE_FUNCTIONS, 'resolveTetherHandleEurW');

export async function resolveTetherHandle(handle: string) {
  const res = await resolveTetherHandleFn({ handle });
  return res.data;
}

// PPL-60 — email → user lookup. Companion to resolveTetherHandle for the
// composer's on-pplkpr detection when the picked contact has no handle but
// has an email (or for "invite someone new" with an email).
const lookupUserByEmailFn = httpsCallable<
  { email: string },
  | { found: false }
  | { found: true; uid: string; displayName: string; tetherHandle: string | null; profileImageUrl: string | null }
>(FIREBASE_FUNCTIONS, 'lookupUserByEmailEurW');

export async function lookupUserByEmail(email: string) {
  const res = await lookupUserByEmailFn({ email });
  return res.data;
}

// PPL-53 — Gemini-backed opener helper for the /rooms composer's
// "ask f8 to help phrase this" button.
const generateRoomOpenerFn = httpsCallable<
  { inviteeName: string; relationship?: string; draft?: string },
  { opener: string }
>(FIREBASE_FUNCTIONS, 'generateRoomOpenerEurW');

export async function generateRoomOpener(params: {
  inviteeName: string;
  relationship?: string;
  draft?: string;
}) {
  const res = await generateRoomOpenerFn(params);
  return res.data;
}

export async function respondToRoomInvite(
  mediationId: string,
  response: 'accept' | 'decline',
) {
  const res = await respondToRoomInviteFn({ mediationId, response });
  return res.data;
}

/**
 * UNAUTHENTICATED — used by the public `/i/:token` landing route. Returns the
 * safe public view only (inviter handle, reason, expired, alreadyAccepted).
 */
export async function resolveRoomInviteByToken(
  token: string,
): Promise<RoomInvitePublicView> {
  const res = await resolveRoomInviteByTokenFn({ token });
  return res.data;
}

export async function acceptRoomInviteByToken(token: string) {
  const res = await acceptRoomInviteByTokenFn({ token });
  return res.data;
}

export async function declineRoomInviteByToken(token: string) {
  const res = await declineRoomInviteByTokenFn({ token });
  return res.data;
}

export async function getMyRooms() {
  const res = await getMyRoomsFn({} as never);
  return res.data;
}

// ─── Meet a match (PPL-171) ──────────────────────────────────────────────────

/** One ranked candidate from getBreakTheIceMatchesEurW (PPL-170). */
export interface MatchCandidate {
  uid: string;
  name: string;
  /** Raw tetherHandle (no @ prefix). May be empty. */
  handle: string;
  avatar: string;
  aboutMe: string;
  sharedInterests: string[];
  city: string;
  lastActiveBucket: 'today' | 'this week' | 'this month';
}

const getBreakTheIceMatchesFn = httpsCallable<
  { limit?: number },
  { candidates: MatchCandidate[] }
>(FIREBASE_FUNCTIONS, 'getBreakTheIceMatchesEurW');

export async function getBreakTheIceMatches(limit = 4): Promise<MatchCandidate[]> {
  const res = await getBreakTheIceMatchesFn({ limit });
  // Normalize per-candidate: MatchView reads sharedInterests (.length/.slice)
  // unguarded, so a candidate missing the field must not crash the match step.
  return (res.data.candidates || []).map((c) => ({
    ...c,
    sharedInterests: Array.isArray(c.sharedInterests) ? c.sharedInterests : [],
  }));
}

// ─── Prep ────────────────────────────────────────────────────────────────────

export async function startRoomPrep(mediationId: string) {
  const res = await startRoomPrepFn({ mediationId });
  return res.data;
}

export async function sendRoomPrepMessage(mediationId: string, text: string) {
  const res = await sendRoomPrepMessageFn({ mediationId, text });
  return res.data;
}

export async function endRoomPrepSolo(mediationId: string) {
  const res = await endRoomPrepSoloFn({ mediationId });
  return res.data;
}

export async function generateRoomPrepChips(mediationId: string) {
  const res = await generateRoomPrepChipsFn({ mediationId });
  return res.data;
}

export async function getMediationInviter(mediationId: string) {
  const res = await getMediationInviterFn({ mediationId });
  return res.data;
}

export async function getMediationCounterpart(mediationId: string) {
  const res = await getMediationCounterpartFn({ mediationId });
  return res.data;
}

// ─── Consent + ready ─────────────────────────────────────────────────────────

export async function updateRoomConsent(params: {
  mediationId: string;
  refPrep?: boolean;
  refHistory?: boolean;
  refPatterns?: boolean;
}) {
  const res = await updateRoomConsentFn(params);
  return res.data;
}

export async function setRoomReady(mediationId: string) {
  const res = await setRoomReadyFn({ mediationId });
  return res.data;
}

// ─── Joint room ──────────────────────────────────────────────────────────────

export async function sendRoomMessage(mediationId: string, text: string) {
  const res = await sendRoomMessageFn({ mediationId, text });
  return res.data;
}

// ─── Room pictures (PPL-186/187) ─────────────────────────────────────────────

const ROOM_IMAGE_MAX_EDGE = 1600;
const ROOM_IMAGE_QUALITY = 0.85;

/**
 * Downscale/compress an image file to a JPEG blob (longest edge ≤1600px) so
 * uploads stay well under the server's 8MB cap and load fast in the room.
 * Falls back to the original file if decoding fails (server still enforces
 * size/type).
 */
async function compressRoomImage(file: File): Promise<Blob> {
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, ROOM_IMAGE_MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    // JPEG has no alpha — fill white first so transparent PNG areas don't
    // re-encode as solid black.
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(bitmap, 0, 0, w, h);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', ROOM_IMAGE_QUALITY),
    );
    return blob ?? file;
  } catch {
    return file;
  }
}

/**
 * Upload a room image and post it as a message. The client only ever sends the
 * STORAGE PATH — the server verifies the object, moderates it, and mints the
 * display URL itself. Returns the send result (check `success`: false + reason
 * is the moderation-held path).
 */
export async function sendRoomImage(
  mediationId: string,
  uid: string,
  file: File,
  opts: { caption?: string; fulfillsRequestId?: string } = {},
) {
  const blob = await compressRoomImage(file);
  const imageId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}.jpg`;
  const storagePath = `mediations/${mediationId}/roomImages/${uid}/${imageId}`;
  const storageRef = ref(FIREBASE_STORAGE, storagePath);
  await uploadBytes(storageRef, blob, { contentType: blob.type || 'image/jpeg' });
  const res = await sendRoomMessageFn({
    mediationId,
    messageType: 'image',
    storagePath,
    ...(opts.caption ? { text: opts.caption } : {}),
    ...(opts.fulfillsRequestId ? { fulfillsRequestId: opts.fulfillsRequestId } : {}),
  });
  return res.data;
}

export async function requestRoomPicture(mediationId: string) {
  const res = await requestRoomPictureFn({ mediationId });
  return res.data;
}

export async function declineRoomPictureRequest(mediationId: string, requestMessageId: string) {
  const res = await respondRoomPictureRequestFn({ mediationId, requestMessageId });
  return res.data;
}

export async function reactToRoomMessage(
  mediationId: string,
  messageId: string,
  emoji: string,
): Promise<void> {
  await sendRoomReactionFn({ mediationId, messageId, emoji });
}

export async function requestMediatorTurn(mediationId: string) {
  const res = await requestMediatorTurnFn({ mediationId });
  return res.data;
}

export async function markRoomSeen(mediationId: string) {
  const res = await markRoomSeenFn({ mediationId });
  return res.data;
}

// ─── Check-ins ───────────────────────────────────────────────────────────────

export async function submitRoomCheckin(
  mediationId: string,
  answer: RoomCheckinAnswer,
) {
  const res = await submitRoomCheckinFn({ mediationId, answer });
  return res.data;
}

// ─── Exit ────────────────────────────────────────────────────────────────────

export async function endRoomKeepSummary(mediationId: string) {
  const res = await endRoomKeepSummaryFn({ mediationId });
  return res.data;
}

export async function pauseRoom(mediationId: string) {
  const res = await pauseRoomFn({ mediationId });
  return res.data;
}

export async function resumeRoom(mediationId: string) {
  const res = await resumeRoomFn({ mediationId });
  return res.data;
}

export async function deleteRoomForBoth(mediationId: string) {
  const res = await deleteRoomForBothFn({ mediationId });
  return res.data;
}

/**
 * Retry synthesis generation after a failure (PPL-46 F9). Resets
 * synthesisStatus from 'failed' → 'pending', then runs the synthesis flow
 * inline. The snapshot listener on the caller's page will pick up the resulting
 * status change automatically.
 */
export async function retrySynthesis(mediationId: string) {
  const res = await retrySynthesisFn({ mediationId });
  return res.data;
}

// ─── Realtime subscribers ────────────────────────────────────────────────────

/**
 * Subscribe to the root mediation doc (state, consent, ready,
 * synthesisStatus, ...). Real-time so consent toggles and ready transitions
 * by the other side appear immediately. Mirrors
 * `messageService.subscribeToMessages`.
 */
export function subscribeToRoom(
  mediationId: string,
  onData: (mediation: Room | null) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  return onSnapshot(
    doc(FIRESTORE_DB, 'mediations', mediationId),
    (snap) => {
      if (!snap.exists()) {
        onData(null);
        return;
      }
      onData({ id: snap.id, ...(snap.data() as Omit<Room, 'id'>) });
    },
    (err) => onError?.(err),
  );
}

/**
 * Subscribe to the caller's own prep thread
 * (`mediations/{id}/prep/{uid}/messages`). Each side only ever reads its own
 * thread — the other side's prep is private until consent surfaces it via an
 * f8 turn in the joint room.
 */
export function subscribeToPrepMessages(
  mediationId: string,
  uid: string,
  onData: (messages: RoomMessage[]) => void,
  onError?: (err: Error) => void,
  onMeta?: (meta: SubscriptionMeta) => void,
): Unsubscribe {
  const q = query(
    collection(FIRESTORE_DB, 'mediations', mediationId, 'prep', uid, 'messages'),
    orderBy('createdAt', 'desc'),
    fbLimit(MESSAGE_LIMIT),
  );
  return onSnapshot(
    q,
    (snap) => {
      const msgs: RoomMessage[] = snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as Omit<RoomMessage, 'id'>),
      }));
      // Subscribers expect chronological order.
      onData(msgs.reverse());
      onMeta?.({ atLimit: snap.size >= MESSAGE_LIMIT });
    },
    (err) => onError?.(err),
  );
}

/** Subscribe to the shared joint-room messages (`mediations/{id}/roomMessages`). */
export function subscribeToRoomMessages(
  mediationId: string,
  onData: (messages: RoomMessage[]) => void,
  onError?: (err: Error) => void,
  onMeta?: (meta: SubscriptionMeta) => void,
): Unsubscribe {
  const q = query(
    collection(FIRESTORE_DB, 'mediations', mediationId, 'roomMessages'),
    orderBy('createdAt', 'desc'),
    fbLimit(MESSAGE_LIMIT),
  );
  return onSnapshot(
    q,
    (snap) => {
      const msgs: RoomMessage[] = snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as Omit<RoomMessage, 'id'>),
      }));
      onData(msgs.reverse());
      onMeta?.({ atLimit: snap.size >= MESSAGE_LIMIT });
    },
    (err) => onError?.(err),
  );
}

/**
 * Subscribe to the caller's own exit summary
 * (`mediations/{id}/exitSummary/{uid}`). Written once by
 * `endMediationKeepSummaryEurW`; each side reads their own doc only.
 */
export function subscribeToExitSummary(
  mediationId: string,
  uid: string,
  onData: (summary: RoomExitSummary | null) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  return onSnapshot(
    doc(FIRESTORE_DB, 'mediations', mediationId, 'exitSummary', uid),
    (snap) => {
      if (!snap.exists()) {
        onData(null);
        return;
      }
      onData({ ...(snap.data() as Omit<RoomExitSummary, 'uid'>), uid: snap.id });
    },
    (err) => onError?.(err),
  );
}
